/**
 * DefinitionCard — one governed definition, read as a reference-manual entry (S4,
 * §7 S4): anchored title, plain-English OVERVIEW, HOW IT'S CALCULATED (the
 * registry formula — never SQL) and DATA SOURCE & LINEAGE (upstream system named
 * in words + the exact warehouse view), then served terms, owner, variants, dq.
 * Governed facts come from the typed registry row (CD-7); the prose comes from
 * ../../content/definitionDocs. `hidden` (with a print:block override) lets the
 * on-screen filter hide a row while the printed pack still renders every one.
 */

import React from 'react'
import clsx from 'clsx'
import type {
  Cadence,
  LockPolicy,
  MetricFormat,
  RegistryMetric,
  SignOff,
} from '../../types/metrics'
import { definitionDocs, sourceViews } from '../../content/definitionDocs'
import TrustChip, { type TrustTone } from './TrustChip'

const SIGN_OFF_TONES: Record<SignOff, TrustTone> = {
  draft: 'warn',
  proposed: 'info',
  signed: 'good',
}

// §4 registry enums rendered as document words, not code values
const FORMAT_TERMS: Record<MetricFormat, string> = {
  gbp_k: '£ thousands',
  pct: 'percentage',
  count: 'count',
  days: 'days',
}
const LOCK_TERMS: Record<LockPolicy, string> = {
  // 'monthly' qualifier is load-bearing: dual-cadence metrics carry this
  // policy but their weekly serving never locks (§4 — no weekly wrapper)
  monthly_eom_minus_3: 'monthly values lock 3 months in arrears (EOM−3)',
  never_locked: 'never locks',
}

interface DefinitionCardProps {
  metric: RegistryMetric
  cadence: Cadence // the section's cadence — picks which lineage views to show
  hidden?: boolean // filtered out on screen; still printed (print:block override)
}

/** '£ thousands · higher is better · group: sum · locks …' — §4 behaviour in plain terms.
 *  Direction is stated HERE because the cell arrow stopped carrying it on 10 Aug 2026 (it
 *  now reports beat/miss, not which way is good) — the property still has to be readable. */
function servedTerms(metric: RegistryMetric): string {
  const parts = [FORMAT_TERMS[metric.format]]
  parts.push(metric.direction === 'higher_is_better' ? 'higher is better' : 'lower is better')
  if (metric.group_rule) parts.push(`group: ${metric.group_rule.replace('_', ' ')}`)
  parts.push(LOCK_TERMS[metric.lock_policy])
  return parts.join(' · ')
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-xs text-slate-700 dark:text-slate-200">{children}</div>
    </div>
  )
}

function CardChips({ metric }: { metric: RegistryMetric }) {
  return (
    <span className="flex flex-shrink-0 flex-wrap items-center justify-end gap-1.5">
      {!metric.matrix && <TrustChip tone="neutral" label="served component" />}
      {metric.drill?.enabled && <TrustChip tone="info" label="drill-ready" />}
      <span data-testid="sign-off-chip">
        <TrustChip tone={SIGN_OFF_TONES[metric.sign_off]} label={metric.sign_off} />
      </span>
    </span>
  )
}

/** DATA SOURCE & LINEAGE — the upstream system(s) named in words, the narrative,
 *  and the exact captured view; honest when no view exists for the cadence. */
function CardLineage({ metric, cadence }: DefinitionCardProps) {
  const views = sourceViews(metric, cadence)
  const source = definitionDocs[metric.id]?.source
  return (
    <div className="mt-3 rounded-md bg-slate-50 p-3 dark:bg-slate-800/40 print:bg-transparent print:p-0">
      <Field label={`Data source & lineage (${cadence})`}>
        {source && <p className="max-w-prose leading-relaxed">{source}</p>}
        {views.length > 0 ? (
          <ul className="mt-1.5 space-y-1">
            {views.map(({ system, detail, view }) => (
              <li key={view} className="flex flex-wrap items-baseline gap-x-1.5">
                <span className="font-semibold text-slate-800 dark:text-slate-100">{system}</span>
                {detail && <span className="text-slate-400 dark:text-slate-500">· {detail}</span>}
                <span className="text-slate-400">→</span>
                <code className="break-all font-mono text-[11px] text-slate-600 dark:text-slate-300">
                  {view}
                </code>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 italic text-slate-500 dark:text-slate-400">
            no source view captured for this cadence
          </p>
        )}
      </Field>
    </div>
  )
}

function CardTerms({ metric }: { metric: RegistryMetric }) {
  return (
    <div className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-3 print:grid-cols-3">
      <Field label="Owner">{metric.owner}</Field>
      <div className="sm:col-span-2 print:col-span-2">
        <Field label="Served terms">{servedTerms(metric)}</Field>
      </div>
    </div>
  )
}

/** Variants, full dq-flag text and registry note — rendered only when present. */
function CardFlags({ metric }: { metric: RegistryMetric }) {
  if (metric.variants.length === 0 && metric.dq_flags.length === 0 && !metric.note) return null
  return (
    <div className="mt-3 space-y-2">
      {metric.variants.length > 0 && (
        <Field label="Variants">
          <span className="font-mono text-[11px]">{metric.variants.join(' · ')}</span>
        </Field>
      )}
      {metric.dq_flags.length > 0 && (
        <Field label="Data quality">
          <ul className="list-disc space-y-0.5 pl-4 text-amber-700 dark:text-amber-400">
            {metric.dq_flags.map((flag) => (
              <li key={flag}>{flag}</li>
            ))}
          </ul>
        </Field>
      )}
      {metric.note && <Field label="Note">{metric.note}</Field>}
    </div>
  )
}

/** Title + chips, the plain-English overview, then the calculation. */
function CardIntro({ metric }: { metric: RegistryMetric }) {
  const overview = definitionDocs[metric.id]?.overview
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">{metric.title}</h3>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            {metric.pillar} · served as <span className="font-mono">“{metric.served_metric}”</span>
          </p>
        </div>
        <CardChips metric={metric} />
      </div>
      {overview && (
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-800 dark:text-slate-100">
          {overview}
        </p>
      )}
      <div className="mt-3">
        <Field label="How it's calculated">
          <p className="max-w-prose text-[13px] leading-relaxed">{metric.formula.trim()}</p>
        </Field>
      </div>
    </>
  )
}

export default function DefinitionCard({
  metric,
  cadence,
  hidden = false,
}: DefinitionCardProps): React.JSX.Element {
  return (
    <article
      id={`def-${cadence}-${metric.id}`}
      data-testid="definition-row"
      data-metric-id={metric.id}
      className={clsx(
        'scroll-mt-24 break-inside-avoid rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 print:rounded-none print:border-0 print:border-b print:p-0 print:pb-3 print:shadow-none',
        hidden && 'hidden print:block'
      )}
    >
      <CardIntro metric={metric} />
      <CardLineage metric={metric} cadence={cadence} />
      <CardTerms metric={metric} />
      <CardFlags metric={metric} />
    </article>
  )
}
